import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, setToken, getToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // `getToken` is a stable module-scope import — safe to omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (getToken()) nav("/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { email, password });
      setToken(res.data.token);
      nav("/admin", { replace: true });
    } catch (e) {
      setErr("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="font-serif text-4xl text-ink tracking-tight">ReGain</div>
          <div className="text-xs uppercase tracking-[0.3em] text-ink-muted mt-2">
            Admin · ReGain Clinic
          </div>
        </div>
        <form
          onSubmit={submit}
          className="bg-surface border border-line rounded-3xl p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-7">
            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
              <Lock className="w-4 h-4 text-sage" strokeWidth={1.75} />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-ink">Sign in</h1>
              <p className="text-xs text-ink-muted">Authorized staff only</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={ADMIN.loginEmail}
                className="bg-cream border-line focus-visible:ring-sage h-11"
                placeholder="admin@regainclinic.in"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={ADMIN.loginPassword}
                className="bg-cream border-line focus-visible:ring-sage h-11"
                placeholder="••••••••"
                required
              />
            </div>

            {err && (
              <div
                data-testid={ADMIN.loginError}
                className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid={ADMIN.loginSubmit}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink hover:bg-sage-dark text-cream px-5 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
